import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import styles from './RowActionsMenu.module.css';

import {
  flattenRowActions,
  groupRowActions,
} from '../../../domain/tableRowActions/tableRowActionsUseCase';
import ColumnsVisibility from '../ColumnsVisibility/ColumnsVisibility';
import SearchGroupList from '../SearchGroupList/SearchGroupList';
import IconButton from '../../atoms/IconButton/IconButton';
import Picto from '../../atoms/Picto/Picto';

/**
 * @typedef {object} RowActionsMenuStackEntry
 * @property {string} lib
 * @property {ApiManagerTableActionsArray} actions
 */

/**
 * @param {object} props
 * @param {string} props.rowId
 * @param {ApiManagerTableActionsArray} props.actions
 * @param {boolean} [props.isLoading=false]
 * @param {(action: TableRowActionFlattened, type: string) => void} props.onSelectAction
 * @returns {JSX.Element}
 */
function RowActionsMenu({
  rowId,
  actions,
  isLoading = false,
  onSelectAction,
}) {
  const { t } = useTranslation();
  const [stack, setStack] = useState(/** @type {RowActionsMenuStackEntry[]} */ ([]));

  const currentLevel = stack.length > 0
    ? stack[stack.length - 1]
    : { lib: '', actions };

  const groupedOptions = useMemo(() => {
    const prefix = stack.length > 0
      ? `${rowId}-sub-${stack.length}`
      : rowId;
    return groupRowActions(flattenRowActions(currentLevel.actions, prefix));
  }, [actions, currentLevel.actions, rowId, stack]);

  const handleBack = () => {
    setStack((prev) => prev.slice(0, -1));
  };

  const handleOpenSubActions = (/** @type {TableRowActionFlattened} */ option) => {
    if (option.type !== 'openSubActions') return;
    const subAction = /** @type {ApiManagerTableActionOpenSubActionsObject} */ (option);
    setStack((prev) => [...prev, { lib: subAction.lib, actions: subAction.actions }]);
  };

  return (
    <div className={styles['row-actions-menu-container']}>
      {stack.length > 0 ? (
        <div className={styles['row-actions-menu-header']}>
          <IconButton
            icon="chevron-left"
            color="transparent"
            size="small"
            onClick={handleBack}
            title={t('tableRowActions.back')}
          />
          <span className={styles['row-actions-menu-header-title']}>
            {currentLevel.lib}
          </span>
        </div>
      ) : null}
      <ColumnsVisibility options={groupedOptions} messageNoOptions={t('table.filters.noResult')}>
        {(label, options) => (
          <SearchGroupList
            key={label}
            hasLabel={!!label}
            items={options}
            label={label}
          >
            {(option) => {
              const isSubMenu = option.type === 'openSubActions';
              return (
                <button
                  key={option.id}
                  type="button"
                  className={isSubMenu
                    ? `${styles['row-actions-menu-item']} ${styles['row-actions-menu-item-sub']}`
                    : styles['row-actions-menu-item']}
                  onClick={() => {
                    if (isSubMenu) {
                      handleOpenSubActions(option);
                    } else {
                      onSelectAction(option, option.type);
                    }
                  }}
                  disabled={isLoading}
                >
                  <span className={styles['row-actions-menu-item-main']}>
                    <IconButton
                      readOnly
                      icon={option.icon}
                      color="transparent"
                      size="small"
                    />
                    <span className={styles['row-actions-menu-label']}>
                      {option.lib}
                    </span>
                  </span>
                  {isSubMenu ? (
                    <Picto icon="chevron-right" className={styles['row-actions-menu-chevron']} />
                  ) : null}
                </button>
              );
            }}
          </SearchGroupList>
        )}
      </ColumnsVisibility>
    </div>
  );
}

export default RowActionsMenu;
